import React, { useEffect, useState } from 'react'
import { HRMessage } from '@/types/chat.types'
import { useAppSelector } from '@/store/hooks'
import { selectStreamingMessage } from '@/store/slices/socketSlice'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import 'highlight.js/styles/github.css'
import '@/styles/highlight-dark.css'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, StopCircle, FileText, CheckCircle2 } from 'lucide-react'
import { FeedbackButton } from '@/components/common/FeedbackButton'
import { feedbackService } from '@/services/feedbackService'
import { useToast } from '@/hooks/useToast'

interface StreamingMessageProps {
  message: HRMessage
  onCancelStream?: (messageId: string) => void
  onToggleSources?: (messageId: string) => void
  showSources?: boolean
}

export const StreamingMessage: React.FC<StreamingMessageProps> = ({
  message,
  onCancelStream,
  onToggleSources,
  showSources = false
}) => {
  const [typingContent, setTypingContent] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  const [currentFeedback, setCurrentFeedback] = useState<{ rating: number; comment: string | null } | null>(null)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const { toast } = useToast()
  
  // Get streaming data from socket state if this message is currently streaming
  const streamingData = useAppSelector(selectStreamingMessage(message.id))
  
  
  // Determine if this message is actively streaming
  const isActivelyStreaming = message.isStreaming && streamingData?.isStreaming
  const streamingContent = streamingData?.content || message.content
  const streamingStatus = streamingData?.status || 'idle'
  const streamingStatusMessage = streamingData?.statusMessage
  // Handle different citation formats between streaming and final message
  const streamingCitations = streamingData?.citations || []
  const messageSources = message.sources || message.messageSources || []
  const messageStreamingCitations = message.streamingCitations || []
  
  // Convert message sources to streaming citation format for consistent display
  const normalizedMessageSources = messageSources.map(source => {
    // Safety check for nested structure
    if (!source.chunk || !source.chunk.document) {
      console.warn('Invalid message source structure:', source)
      return {
        documentId: 'unknown',
        documentTitle: 'Unknown Document',
        pageNumber: undefined,
        relevanceScore: source.relevanceScore || 0,
        snippet: source.citationText || 'No content available'
      }
    }
    
    return {
      documentId: source.chunk.document.id,
      documentTitle: source.chunk.document.title,
      pageNumber: source.chunk.document.pageNumber || source.chunk.pageNumber, // Preserve page numbers
      relevanceScore: source.relevanceScore,
      snippet: source.citationText || source.chunk.text?.substring(0, 200) + '...'
    }
  })
  
  // Deduplicate streaming citations by documentId to prevent duplicates during streaming
  const deduplicatedStreamingCitations = streamingCitations.reduce((acc, citation) => {
    const existing = acc.find(c => c.documentId === citation.documentId)
    if (!existing) {
      acc.push(citation)
    }
    return acc
  }, [] as typeof streamingCitations)

  // Use the best available citation source with consistent logic
  // Priority: streaming citations (during active streaming) -> stored streaming citations -> message sources
  const citations = isActivelyStreaming && deduplicatedStreamingCitations.length > 0
    ? deduplicatedStreamingCitations
    : messageStreamingCitations.length > 0
      ? messageStreamingCitations
      : normalizedMessageSources.length > 0
        ? normalizedMessageSources
        : deduplicatedStreamingCitations // Fallback to streaming citations even after streaming ends
  

  // Determine what content to show and whether we're actively streaming
  const shouldShowContent = streamingContent || message.content
  const effectivelyStreaming = isActivelyStreaming && streamingData && streamingStatus !== 'complete'

  // Typewriter effect for streaming content
  useEffect(() => {
    if (!effectivelyStreaming) {
      setTypingContent(shouldShowContent || '')
      setShowCursor(false)
      return
    }

    let currentIndex = typingContent.length
    const targetContent = streamingContent

    if (currentIndex < targetContent.length) {
      const timer = setTimeout(() => {
        setTypingContent(targetContent.slice(0, currentIndex + 1))
      }, 20) // Adjust speed as needed

      return () => clearTimeout(timer)
    }
  }, [streamingContent, typingContent.length, effectivelyStreaming])

  // Cursor blinking effect
  useEffect(() => {
    if (!effectivelyStreaming) return

    const interval = setInterval(() => {
      setShowCursor(prev => !prev)
    }, 500)

    return () => clearInterval(interval)
  }, [effectivelyStreaming])

  // Load existing feedback for this message
  useEffect(() => {
    const loadFeedback = async () => {
      // Only load feedback for completed assistant messages
      if (message.role !== 'ASSISTANT' || effectivelyStreaming) return
      
      try {
        setFeedbackLoading(true)
        const feedback = await feedbackService.getMessageFeedback(message.id)
        setCurrentFeedback(feedback)
      } catch (error) {
        console.error('Failed to load feedback:', error)
      } finally {
        setFeedbackLoading(false)
      }
    }

    loadFeedback()
  }, [message.id, message.role, effectivelyStreaming])

  const handleSubmitFeedback = async (rating: number, comment?: string) => {
    try {
      const feedback = await feedbackService.submitFeedback({
        messageId: message.id,
        rating,
        comment
      })
      
      setCurrentFeedback({
        rating: feedback.rating,
        comment: feedback.comment
      })
      
      toast.success('Feedback submitted', 'Thank you for your feedback! It helps us improve our responses.')
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      toast.error('Failed to submit feedback', 'Please try again later.')
      throw error // Re-throw so the UI can handle loading states
    }
  }

  const handleCancelStream = () => {
    if (onCancelStream && effectivelyStreaming) {
      onCancelStream(message.id)
    }
  }

  const formatStatusMessage = (status: string, message?: string) => {
    const result = (() => {
      switch (status) {
        case 'searching':
          return message || 'Searching HR documents...'
        case 'generating':
          return message || 'Generating response...'
        case 'processing':
          return message || 'Processing your request...'
        case 'complete':
          return 'Response complete'
        case 'error':
          return message || 'An error occurred'
        default:
          return message || ''
      }
    })()
    
    
    return result
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'searching':
      case 'generating':
      case 'processing':
        return <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-2 p-4 bg-muted rounded-lg">
      {/* Status indicator */}
      {effectivelyStreaming && streamingStatus !== 'idle' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          {getStatusIcon(streamingStatus)}
          <span>{formatStatusMessage(streamingStatus, streamingStatusMessage)}</span>
          {effectivelyStreaming && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelStream}
              className="ml-auto text-destructive hover:text-destructive/80"
            >
              <StopCircle className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          )}
        </div>
      )}

      {/* Message content */}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        {shouldShowContent ? (
          <div className="relative">
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {shouldShowContent}
            </ReactMarkdown>
            {effectivelyStreaming && showCursor && (
              <span className="inline-block w-0.5 h-5 bg-blue-500 ml-1 animate-pulse" />
            )}
          </div>
        ) : effectivelyStreaming ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : null}
      </div>

      {/* Error state */}
      {message.error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{message.error}</span>
        </div>
      )}

      {/* Citations (show during streaming if available) */}
      {citations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Sources ({citations.length})
            </span>
            {onToggleSources && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleSources(message.id)}
                className="text-primary hover:text-primary/80"
              >
                <FileText className="w-4 h-4 mr-1" />
                {showSources ? 'Hide' : 'Show'} Sources
              </Button>
            )}
          </div>

          {showSources && (
            <div className="space-y-2">
              {citations.map((citation, index) => (
                <div
                  key={index}
                  className="p-2 bg-muted border border-border rounded text-sm"
                >
                  <div className="font-medium text-foreground">
                    {citation.documentTitle}
                  </div>
                  {citation.pageNumber && (
                    <div className="text-muted-foreground text-xs">
                      Page {citation.pageNumber}
                    </div>
                  )}
                  {citation.snippet && (
                    <div className="text-foreground mt-1 text-xs italic">
                      "{citation.snippet}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Show loading citations during streaming */}
      {effectivelyStreaming && citations.length === 0 && streamingStatus === 'searching' && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="animate-spin w-3 h-3 border border-muted-foreground border-t-transparent rounded-full" />
            <span>Looking for relevant sources...</span>
          </div>
        </div>
      )}

      {/* Feedback buttons for completed assistant messages */}
      {message.role === 'ASSISTANT' && !effectivelyStreaming && shouldShowContent && (
        <div className="mt-3 pt-2 border-t border-border/50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Was this response helpful?</span>
            <FeedbackButton
              messageId={message.id}
              currentFeedback={currentFeedback}
              onSubmitFeedback={handleSubmitFeedback}
              disabled={feedbackLoading}
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default StreamingMessage